/**
 * DopesheetLegendPopover — legend/guide popover for the keyframe editor.
 *
 * Explains the editor modes, icon meanings, and keyframe colors.
 * Triggered by a ? (HelpCircle) button in the graph panel header.
 */

import { HelpCircle, LineChart, Lock, Timer } from 'lucide-react'
import { Button } from '@/components/ui/button'
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover'

interface DopesheetLegendPopoverProps {
  disabled?: boolean
}

export function DopesheetLegendPopover({ disabled }: DopesheetLegendPopoverProps) {
  return (
    <Popover>
      <PopoverTrigger asChild>
        <Button
          variant="ghost"
          size="sm"
          className="h-6 w-6 p-0"
          disabled={disabled}
          aria-label="编辑器说明"
          title="编辑器说明"
        >
          <HelpCircle className="h-3 w-3" />
        </Button>
      </PopoverTrigger>
      <PopoverContent align="end" className="w-[260px] text-xs">
        <p className="mb-2 text-xs font-medium text-foreground">关键帧编辑器说明</p>

        <p className="mb-1 text-[10px] font-semibold uppercase tracking-wide text-muted-foreground">
          编辑模式
        </p>
        <ul className="mb-2 space-y-1 text-muted-foreground">
          <li><strong className="text-foreground">曲线</strong> — 在曲线图上拖拽控制点，精确调整缓动曲线</li>
          <li><strong className="text-foreground">时间表</strong> — 在 Dopesheet 上管理关键帧的位置和时序</li>
          <li><strong className="text-foreground">拆分</strong> — 同时显示曲线图和时间表</li>
        </ul>

        <p className="mb-1 text-[10px] font-semibold uppercase tracking-wide text-muted-foreground">
          图标说明
        </p>
        <ul className="mb-2 space-y-1 text-muted-foreground">
          <li className="flex items-start gap-1.5">
            <LineChart className="mt-px h-3 w-3 shrink-0 text-orange-500" />
            <span>曲线编辑器 — 当前选中属性的缓动曲线</span>
          </li>
          <li className="flex items-start gap-1.5">
            <Lock className="mt-px h-3 w-3 shrink-0 text-red-400" />
            <span>转场锁定 — 过渡区域内不可添加关键帧</span>
          </li>
          <li className="flex items-start gap-1.5">
            <Timer className="mt-px h-3 w-3 shrink-0" />
            <span>自动关键帧 — 属性变化时自动创建关键帧</span>
          </li>
          <li className="flex items-start gap-1.5">
            <span className="mt-0.5 block h-2 w-2 shrink-0 rotate-45 bg-primary" />
            <span>关键帧菱形标记</span>
          </li>
        </ul>

        <p className="mb-1 text-[10px] font-semibold uppercase tracking-wide text-muted-foreground">
          颜色说明
        </p>
        <ul className="space-y-1 text-muted-foreground">
          <li className="flex items-start gap-1.5">
            <span className="mt-0.5 block h-2 w-2 shrink-0 rotate-45 bg-orange-500" />
            <span>默认关键帧</span>
          </li>
          <li className="flex items-start gap-1.5">
            <span className="mt-0.5 block h-2 w-2 shrink-0 rotate-45 bg-orange-500 shadow-[0_0_0_1px_rgba(249,115,22,0.45)]" />
            <span>选中关键帧</span>
          </li>
        </ul>
      </PopoverContent>
    </Popover>
  )
}
